/**
 * A compact date-range trigger field: shows the selected range as
 * `dd.MM.yyyy – dd.MM.yyyy` (R18.10) in a small input-like control, and opens
 * the existing {@link InlineDateRangePicker} as a popover on click instead of
 * rendering the calendar inline.
 *
 * The calendar itself is not reimplemented here — every selection rule (click
 * start, click end, hover preview, clear, Escape) still lives in
 * `InlineDateRangePicker`. This component only supplies the closed-state
 * trigger and the open/close plumbing around it, following the same Radix
 * `Popover` pattern `SavedViewMenu` uses for its dropdown.
 */

import { useState } from 'react';
import { Popover } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate, formatDateRange } from '@/shared/format/date-format';
import { InlineDateRangePicker, type DateRange } from './InlineDateRangePicker';

export interface DateRangeFieldProps {
  /** Current selected range. */
  readonly value?: DateRange | null;
  /** Callback when the range changes (passed straight through from the calendar). */
  readonly onChange: (range: DateRange | null) => void;
  /** Initial month the popover's calendar opens on. */
  readonly initialMonth?: Date;
  /** Minimum selectable date (ISO string). */
  readonly minDate?: string;
  /** Maximum selectable date (ISO string). */
  readonly maxDate?: string;
  /** The accessible name for the trigger button; caller supplies, already resolved (R18.9). */
  readonly label: string;
  /** Additional CSS classes for the trigger button. */
  readonly className?: string;
}

export function DateRangeField({
  value,
  onChange,
  initialMonth,
  minDate,
  maxDate,
  label,
  className,
}: DateRangeFieldProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const displayText =
    value == null
      ? t('datePicker.rangePlaceholder')
      : value.startDate === value.endDate
        ? formatDate(value.startDate)
        : formatDateRange(value.startDate, value.endDate);

  // A completed two-distinct-day range closes the popover on its own so picking
  // a range does not need a separate dismiss step; picking just the start day
  // (start === end, still awaiting the second click) or clearing the selection
  // leaves the popover open, since both are mid-interaction rather than done.
  const handleChange = (range: DateRange | null): void => {
    onChange(range);
    if (range && range.startDate !== range.endDate) {
      setOpen(false);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            'border-line bg-panel inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-control)] border px-2 text-[12.5px] text-txt outline-none focus-visible:border-[var(--accent)]',
            value == null && 'text-txt-subtle',
            className,
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-txt-subtle" aria-hidden="true" />
          <span className="whitespace-nowrap">{displayText}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} className="z-50">
          <InlineDateRangePicker
            value={value ?? null}
            onChange={handleChange}
            {...(initialMonth ? { initialMonth } : {})}
            {...(minDate !== undefined ? { minDate } : {})}
            {...(maxDate !== undefined ? { maxDate } : {})}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
